import { nextTickWithResult } from './utils/next-tick.fn';
import { timer } from './utils/timer.fn';

export type Callback<T> = (event: T) => void;
export type VoidCallback = () => void;

export interface EventEmitter<
  EventCallbackMap extends { [key: string]: VoidCallback | Callback<any> }
> {
  on<TKey extends keyof EventCallbackMap>(
    name: TKey,
    func: EventCallbackMap[TKey]
  ): void;
  once<TKey extends keyof EventCallbackMap>(
    name: TKey,
    func: EventCallbackMap[TKey]
  ): void;
  onceAsync<TKey extends keyof EventCallbackMap>(
    name: TKey
  ): Promise<Parameters<EventCallbackMap[TKey]>[0]>;
  onceIf<TKey extends keyof EventCallbackMap>(
    name: TKey,
    predicate: (args: Parameters<EventCallbackMap[TKey]>[0]) => boolean
  ): Promise<Parameters<EventCallbackMap[TKey]>[0]>;
  off<TKey extends keyof EventCallbackMap>(
    name: TKey,
    func: EventCallbackMap[TKey]
  ): void;
  emit<TKey extends keyof EventCallbackMap>(
    name: TKey,
    args?: Parameters<EventCallbackMap[TKey]>[0]
  ): Promise<void>;
}

class EventEmitterImplementation<
  EventCallbackMap extends { [key: string]: VoidCallback | Callback<any> }
> implements EventEmitter<EventCallbackMap>
{
  private events: {
    [P in keyof EventCallbackMap]: EventCallbackMap[P][];
  } = {} as any;

  private ensureArrayExists<TKey extends keyof EventCallbackMap>(name: TKey) {
    if (this.events[name] === undefined) {
      this.events[name] = [];
    }
  }

  private addEvent<TKey extends keyof EventCallbackMap>(
    name: TKey,
    func: EventCallbackMap[TKey]
  ) {
    this.ensureArrayExists(name);
    this.events[name].push(func);
  }

  private removeEvent<TKey extends keyof EventCallbackMap>(
    name: TKey,
    func: EventCallbackMap[TKey]
  ) {
    this.ensureArrayExists(name);
    const idx = this.events[name].indexOf(func);
    this.events[name].splice(idx, 1);
  }

  on<TKey extends keyof EventCallbackMap>(
    name: TKey | TKey[],
    func: EventCallbackMap[TKey]
  ) {
    if (Array.isArray(name)) {
      name.forEach((n) => this.addEvent(n, func));
    } else {
      this.addEvent(name, func);
    }
  }

  once<TKey extends keyof EventCallbackMap>(
    name: TKey,
    func: EventCallbackMap[TKey]
  ) {
    const onceWrapper = ((args) => {
      func(args);
      this.removeEvent(name, onceWrapper);
    }) as EventCallbackMap[TKey];

    this.addEvent(name, onceWrapper);
  }

  onceAsync<TKey extends keyof EventCallbackMap>(
    name: TKey
  ): Promise<Parameters<EventCallbackMap[TKey]>[0]> {
    return new Promise<Parameters<EventCallbackMap[TKey]>[0]>((resolve) => {
      const onceWrapper = ((args) => {
        resolve(args);

        this.removeEvent(name, onceWrapper);
      }) as EventCallbackMap[TKey];

      this.addEvent(name, onceWrapper);
    });
  }

  onceIf<TKey extends keyof EventCallbackMap>(
    name: TKey,
    predicate: (args: Parameters<EventCallbackMap[TKey]>[0]) => boolean
  ): Promise<Parameters<EventCallbackMap[TKey]>[0]> {
    return new Promise<Parameters<EventCallbackMap[TKey]>[0]>((resolve) => {
      const onceWrapper = ((args) => {
        if (!predicate(args)) {
          return;
        }

        this.removeEvent(name, onceWrapper);

        resolve(args);
      }) as EventCallbackMap[TKey];

      this.addEvent(name, onceWrapper);
    });
  }

  off<TKey extends keyof EventCallbackMap>(
    name: TKey,
    func: EventCallbackMap[TKey]
  ) {
    if (!this.events[name]) {
      return;
    }

    this.removeEvent<TKey>(name, func);
  }

  async emit<TKey extends keyof EventCallbackMap>(
    name: TKey,
    args?: Parameters<EventCallbackMap[TKey]>[0]
  ) {
    if (!this.events[name]) {
      return;
    }

    await Promise.all(
      this.events[name].map((fn) =>
        nextTickWithResult(
          async () =>
            await timer<void>(
              `${String(name)}-handler`,
              async () => await fn(args as any)
            )
        )
      )
    );
  }
}

export function createEventEmitter<
  EventCallbackMap extends { [key: string]: VoidCallback | Callback<any> }
>() {
  return new EventEmitterImplementation<EventCallbackMap>();
}
